<template>
  <section
    v-if="data.isTimerActive"
    class="i-report-countdown"
    :class="{
      'fade-in': props.hasFadeInAnimation
    }"
  >
    <div class="wrap">
      <template v-if="timerDisplay">
        <div class="text">
          Your free report will expire in <span class="time">{{ formattedTime }}</span>
        </div>
      </template>
      <template v-else>
        <div class="text">Your Personalized Career Report is Ready to View</div>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect, reactive } from "vue"

// EXPOSED
defineExpose({
  makeActive: () => {
    startTimer()
    data.isTimerActive = true
  }
})

// PROPS
const props = defineProps({
  timerDurationMin: {
    type: Number,
    default: 1
  },
  hasCallToAction: {
    type: Boolean,
    default: true
  },
  hasFadeInAnimation: {
    type: Boolean,
    default: false
  }
})

// EMITS
defineEmits(["scrollToPaymentTable"])

// DATA
const timeRemaining = ref(props.timerDurationMin * 60)
const data = reactive({
  isTimerActive: false
})

// COMPUTED
const formattedTime = computed(() => {
  const minutes = Math.floor((timeRemaining.value / 60) % 60)
  const seconds = Math.floor(timeRemaining.value % 60)

  return `${minutes < 1 ? "" : minutes == 1 ? "1 min" : minutes + " mins"} ${seconds < 10 ? "0" + seconds : seconds} secs`
})

const timerDisplay = computed(() => {
  return timeRemaining.value > 0 && data.isTimerActive
})

//FUNCTIONS
function startTimer() {
  const intervalId = setInterval(() => {
    timeRemaining.value--
    if (timeRemaining.value >= 0) {
      localStorage.setItem("report-countdown", timeRemaining.value.toString())
    }
    if (timeRemaining.value === 0) {
      clearInterval(intervalId)
    }
  }, 1000)
}

// WATCHERS
watchEffect(() => {
  const timer = localStorage.getItem("report-countdown")
  if (timer !== null) {
    timeRemaining.value = parseInt(timer)
  } else {
    localStorage.setItem("report-countdown", timeRemaining.value.toString())
  }
})
</script>

<style scoped lang="scss">
$min-content-width: 960px;
$max-content-width: 1200px;

.i-report-countdown {
  position: sticky;
  top: 0;
  width: 100%;
  z-index: $z-index-overlay;
  background-image: linear-gradient(0deg, #0009 0%, #0009 100%), linear-gradient(90deg, #068eff 0%, #ab2af3 100%);
  padding: 0 5px;

  @media screen and (min-width: $breakpoint-sm) {
    padding: 0 10px;
  }

  @media screen and (min-width: $breakpoint-md) {
    padding: 0 20px;
  }

  &.fade-in {
    animation: fade-in 2s ease 0s 1 normal forwards;
  }

  @keyframes fade-in {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  .wrap {
    @include flex(row, center, center);
    @include text-xs;

    color: $white;
    max-width: 1152px;
    margin: 0 auto;
    padding: 8px 0;
    font-weight: 300;

    @media screen and (min-width: $breakpoint-sm) {
      @include text-sm;

      padding: 10px 0;
    }

    .text {
      @include flex(row, center, center, wrap, 2px 4px);

      .time {
        min-width: 94px;
        text-align: center;
        font-weight: 700;

        @media screen and (min-width: $breakpoint-sm) {
          min-width: 110px;
        }
      }
    }
  }
}
</style>
